import {
  CompressOutlined,
  DownloadOutlined,
  EditOutlined,
  FileAddOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { App, Button, Col, Pagination, Row, Select, Spin, Table, Tag, Tooltip, Upload } from 'antd';
import { debounce } from 'lodash';
import moment from 'moment';
import queryString from 'query-string';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { useLocation, useNavigate } from 'react-router-dom';
import { setCompanies } from '../../contextRedux/Company/CompanyStore';
import { updateProps } from '../../contextRedux/Contract/ContractModel';
import { setContracts } from '../../contextRedux/Contract/ContractStore';
import { setStatusContractes } from '../../contextRedux/StatusContract/StatusContractStore';
import { AnimateButton } from '../../customComponents/button/CustomButton';
import { SearchInput } from '../../customComponents/input/SearchInput';
import { Contract } from '../../DTOS/Contract/Response/Contract';
import CompanyService from '../../services/company/CompanyService';
import ConfigurationService from '../../services/configuration/ConfigurationService';
import ContractService from '../../services/contract/ContractService';
import ContractAdditionalActService from '../../services/contractAdditionalAct/ContractAdditionalActService';
import { RootState } from '../../store';
import { saveBlobCrossPlatform } from '../../utils/saveBlobCrossPlatform';
import { usePermission } from '../../utils/usePermission';
import './Contract.css';
import ContractInformation from './ContractInformation';

const ContractPage: React.FC = () => {
  const isMobile = useMediaQuery({ maxWidth: 720 });
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const searchTerm = useSelector((state: RootState) => state?.models?.contract?.props?.searchTerm);
  const skip = useSelector((state: RootState) => state?.models?.contract?.props?.skip);
  const take = useSelector((state: RootState) => state?.models?.contract?.props?.take);
  const refresh = useSelector((state: RootState) => state?.models?.contract?.props?.refresh);
  const reduxContract = useSelector((state: RootState) => state?.models?.contract);
  const contracts: any = useSelector((state: RootState) => state?.store?.contract?.data ?? []);
  const companies = useSelector((state: RootState) => state?.store?.company?.data ?? []);
  const contractStatuses = useSelector((state: RootState) => state?.store?.statusContract?.data ?? []);

  const [isSearchTermInitialized, setIsSearchTermInitialized] = useState<boolean>(false);
  const [modalInformation, setModalInformation] = useState<boolean>(false);
  const [selectedRecord, setSelectedRecord] = useState<Contract | null>(null);
  const [selectedRecordAdditionalAct, setSelectedRecordAdditionalAct] = useState<Contract | null>(null);
  const [visibleMenuRecord, setVisibleMenuRecord] = useState<string | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<string | undefined>(undefined);
  const [selectedStatus, setSelectedStatus] = useState<string | undefined>(undefined);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'ascend' | 'descend' | null>(null);

  const previousParamsRef = useRef({ term: '', page: '', pageSize: '' });
  const { message } = App.useApp();
  const hasAdminPermission = usePermission(['admin']);
  const menuRef = useRef<any>(null);

  const currentPage = Math.floor(reduxContract?.props?.skip / reduxContract?.props?.take) + 1;
  const pageSizes = [10, 20, 30, 50];

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
      setVisibleMenuRecord(null);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  useEffect(() => {
    const queryParams = queryString.parse(location.search);

    const termInUrl = (queryParams.term as string) || '';
    // Convert page/pageSize from URL to skip/take
    const pageInUrl = Number(queryParams.page) || 1;
    const pageSizeInUrl = Number(queryParams.pageSize) || take; // fallback to default take if not provided
    // Calculate skip based on pageInUrl and pageSizeInUrl
    const calculatedSkip = (pageInUrl - 1) * pageSizeInUrl;
    // Dispatch updates to redux store
    dispatch(updateProps({ field: 'searchTerm', value: termInUrl }));
    dispatch(updateProps({ field: 'skip', value: calculatedSkip }));
    dispatch(updateProps({ field: 'take', value: pageSizeInUrl }));

    // Store these initial values in a ref to avoid extra triggers
    previousParamsRef.current = { term: termInUrl, page: pageInUrl.toString(), pageSize: pageSizeInUrl.toString() };
    setIsSearchTermInitialized(true);
  }, [dispatch, location.search, take]);

  useEffect(() => {
    if (isSearchTermInitialized) {
      const queryParams = queryString.parse(location.search);
      // Derive page number from skip/take
      const currentPage = Math.floor(skip / take) + 1;
      // Only update URL if something changed
      if (
        searchTerm !== previousParamsRef.current.term ||
        currentPage.toString() !== previousParamsRef.current.page ||
        take.toString() !== previousParamsRef.current.pageSize
      ) {
        const params = {
          ...queryParams,
          term: searchTerm || undefined,
          page: currentPage > 1 ? currentPage.toString() : undefined,
          pageSize: take !== 10 ? take.toString() : undefined, // only set if different from default
        };
        const newSearch = queryString.stringify(params);
        navigate(`${location.pathname}?${newSearch}`, { replace: true });
        // Update the ref to the new state
        previousParamsRef.current = {
          term: searchTerm || '',
          page: currentPage.toString(),
          pageSize: take.toString(),
        };
      }
    }
  }, [searchTerm, skip, take, isSearchTermInitialized, navigate, location.pathname, location.search]);

  const {
    data: _dates,
    refetch,
    isFetching: isContractsLoading,
  } = useQuery({
    queryKey: ['contracts', searchTerm, skip, take, refresh, sortField, sortOrder, selectedCompany, selectedStatus],
    queryFn: () =>
      ContractService.search({
        isStatusIncluded: true,
        isCompanyIncluded: true,
        isContractWorkPlaceIncluded: true,
        termen: searchTerm || undefined,
        selectedCompany: selectedCompany,
        selectedStatus: selectedStatus,
        skip,
        take,
        sortField,
        sortOrder: sortOrder === 'ascend' ? 'asc' : sortOrder === 'descend' ? 'desc' : undefined,
      }).then((data) => {
        dispatch(updateProps({ field: 'totalItems', value: data?.totalItems || 0 }));
        dispatch(setContracts(data?.response));
        return data?.response;
      }),
    enabled: isSearchTermInitialized,
    refetchOnWindowFocus: false,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const { data: _selectedRecordAdditionalAct, isFetching: isAdditionalActLoading } = useQuery({
    queryKey: ['contractAdditionalAct', selectedRecord?.id],
    queryFn: () =>
      ContractAdditionalActService.search({
        contractId: selectedRecord?.id!,
        isMetadataIncluded: true,
      })
        .then((data) => {
          setSelectedRecordAdditionalAct(data?.response[0]);
          return data?.response; // You can adjust the response here based on the data shape
        })
        .catch(() => {
          setSelectedRecordAdditionalAct(selectedRecord);
        }),
    enabled: !!selectedRecord,
    refetchOnWindowFocus: false,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const { data: _companies, isFetching: isCompaniesLoading } = useQuery({
    queryKey: ['companies'],
    queryFn: () =>
      CompanyService.search({
        isActive: true,
      }).then((data) => {
        dispatch(setCompanies(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  // Fetch contract statuses for filter
  const { data: _contractStatuses, isFetching: isContractStatusesLoading } = useQuery({
    queryKey: ['contractStatuses'],
    queryFn: () =>
      ConfigurationService.searchContractStatus({}).then((data) => {
        dispatch(setStatusContractes(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  useEffect(() => {
    if (isSearchTermInitialized && searchTerm === '') {
      refetch();
    }
  }, [searchTerm, isSearchTermInitialized, refetch]);

  const handleMenuVisibility = (recordId: string) => {
    setVisibleMenuRecord(visibleMenuRecord === recordId ? null : recordId);
  };

  const handleOptionClick = (action: () => void) => {
    action();
    setVisibleMenuRecord(null); // Hide the menu after performing the action
  };

  const handleModalInformationOpen = (record: any) => {
    setSelectedRecord(record);
    setModalInformation(true);
  };

  const handleModalInformationClose = () => {
    setModalInformation(false);
  };

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      dispatch(updateProps({ field: 'searchTerm', value }));
      dispatch(updateProps({ field: 'skip', value: 0 }));
    }, 1200),
    [dispatch],
  );

  const handleSearch = (value: string) => {
    debouncedSearch(value);
  };

  const handleCompanyChange = (value: string | undefined) => {
    setSelectedCompany(value);
    dispatch(updateProps({ field: 'skip', value: 0 }));
  };

  const handleStatusChange = (value: string | undefined) => {
    setSelectedStatus(value);
    dispatch(updateProps({ field: 'skip', value: 0 }));
  };

  const { mutate: printPdfContract, isPending: isPrintingContract } = useMutation({
    mutationFn: (record: any) =>
      ContractService.print({
        ids: [record.id],
        isStatusIncluded: true,
        isCompanyIncluded: true,
        isContractWorkPlaceIncluded: true,
        isAccountManagerIncluded: true,
        isAccountSaleIncluded: true,
      }),
    onSuccess: (response: any) => {
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'file.pdf';

      if (contentDisposition) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }

      return response.blob().then((blob: any) => {
        saveBlobCrossPlatform(blob, filename);
      });
    },
    onError: () => {
      message.error('Eroare procesare request.');
    },
  });

  const { mutate: updateAllContracts, isPending: isUpdatingAllContract } = useMutation({
    mutationFn: () => ContractService.updateAllContract(),
    onSuccess: () => {
      message.success('S-a finalizat cu succes');
    },
    onError: () => {
      message.error('Eroare procesare request.');
    },
  });

  const printToPdf = (record: any) => {
    printPdfContract(record);
  };

  const { mutate: downloadPdfContract, isPending: isDownloadingPmContract } = useMutation({
    mutationFn: (record: any) => ContractService.downloadFile(record.id),
    onSuccess: (response: any) => {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'file.pdf';

      if (contentDisposition) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }

      return response.blob().then((blob: any) => {
        saveBlobCrossPlatform(blob, filename);
      });
    },
    onError: () => {
      message.error('Eroare procesare request.');
    },
  });

  const downloadContract = (record: any) => {
    downloadPdfContract(record);
  };

  const handleUpdate = async (id: any, file: any) => {
    const formData = new FormData();
    formData.append('id', id);
    formData.append('file', file);

    try {
      const response = await ContractService.upload(formData);

      const data = await response.json();
      if (data?.errors?.length > 0) {
        message.error(data?.errors[0]);
      } else {
        message.success('PDF incarcat!');
        refetch();
      }
    } catch (error: any) {
      message.error(error.message);
    }
  };

  const beforeUpload = (id: any, file: any) => {
    const isPdf = file.type === 'application/pdf';
    if (!isPdf) {
      message.error('Poti adauga doar PDF!');
      return Upload.LIST_IGNORE;
    }
    handleUpdate(id, file);
    return false;
  };

  const getUploadProps = (id: any) => ({
    listType: 'text' as 'text',
    maxCount: 1,
    showUploadList: false,
    beforeUpload: (file: any) => beforeUpload(id, file),
    onChange: (info: any) => {
      if (info.file.status === 'done') {
        handleUpdate(id, info.file);
      } else if (info.file.status === 'error') {
        message.error(`${info.file.name} imaginea nu a fost incarcata.`);
      }
      setVisibleMenuRecord(null);
    },
  });

  const handleModifyAllContracts = () => {
    updateAllContracts();
  };

  const paginationConfig = {
    current: currentPage,
    pageSize: take,
    total: reduxContract?.props.totalItems,
    onChange: (page: number, pageSize: number) => {
      const skipValue = (page - 1) * pageSize;
      dispatch(updateProps({ field: 'skip', value: skipValue }));
      dispatch(updateProps({ field: 'take', value: pageSize }));
    },
    showSizeChanger: true,
    pageSizeOptions: pageSizes.map((size) => size.toString()),
    onShowSizeChange: (_current: number, size: number) => {
      dispatch(updateProps({ field: 'skip', value: 0 }));
      dispatch(updateProps({ field: 'take', value: size }));
    },
    showQuickJumper: false,
  };

  const columns: any[] = [
    {
      title: 'Contract Id',
      dataIndex: 'id',
      key: 'id',
      hidden: true,
    },
    {
      title: 'Numar',
      dataIndex: 'number',
      key: 'number',
      render: (number: string) => (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignContent: 'center',
          }}
        >
          <Tag color={'blue'}>{number}</Tag>
        </div>
      ),
    },
    {
      title: 'Client',
      dataIndex: '',
      key: 'company',
      render: (record: any) => {
        const company = record?.metadata?.company ?? record?.company;
        return (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignContent: 'center',
            }}
          >
            <Tag color={'blue'}>{company?.name}</Tag>
          </div>
        );
      },
    },
    {
      title: 'Numar de inregistrare',
      dataIndex: 'registrationNumber',
      key: 'registrationNumber',
      render: (registrationNumber: string) => (
        <div>
          <span>{registrationNumber}</span>
        </div>
      ),
    },
    {
      title: 'Sisteme',
      dataIndex: '',
      key: 'systemTypes',
      render: (record: any) => {
        const contractWorkPlaceItems = record.contractWorkPlaceItems ?? [];

        const uniqueSystemTypeNames = Array.from(
          new Set(
            contractWorkPlaceItems
              .map((item: any) => item.systemType?.name)
              .filter((name: string | undefined) => !!name),
          ),
        );

        const displayCount = 3;
        const shouldShowEllipsis = uniqueSystemTypeNames.length > displayCount;
        const displayNames = shouldShowEllipsis ? uniqueSystemTypeNames.slice(0, displayCount) : uniqueSystemTypeNames;
        const fullList = uniqueSystemTypeNames.join(', ');

        return (
          <Tooltip title={fullList}>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                maxWidth: '100%',
              }}
            >
              {displayNames.map((name: any, index: any) => (
                <Tag color='blue' key={index}>
                  {name}
                </Tag>
              ))}
              {shouldShowEllipsis && <Tag color='blue'>...</Tag>}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Data Contract',
      dataIndex: 'date',
      key: 'date',
      sorter: true,
      render: (contractDate: string) => (
        <td data-label='Data Contract'>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignContent: 'center',
            }}
          >
            <Tag color={'blue'}>{moment(contractDate).format('DD-MM-YYYY')}</Tag>
          </div>
        </td>
      ),
    },
    {
      title: 'Data Inceput',
      dataIndex: 'dateStart',
      key: 'dateStart',
      sorter: true,
      render: (dateStart: string) => moment(dateStart).format('DD-MM-YYYY'),
    },
    {
      title: 'Data Sfarsit',
      dataIndex: 'dateEnd',
      key: 'dateEnd',
      sorter: true,
      render: (dateEnd: string) => moment(dateEnd).format('DD-MM-YYYY'),
    },
    {
      title: 'Perioada Contractuala (Luni)',
      dataIndex: 'datePeriod',
      key: 'datePeriod',
      render: (text: string) => (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignContent: 'center',
          }}
        >
          <Tag color={'blue'}>{text}</Tag>
        </div>
      ),
    },
    {
      title: 'Achizitie',
      dataIndex: 'acquisitionType',
      key: 'acquisitionType',
    },
    {
      title: 'Status',
      dataIndex: 'enumContractStatusId',
      key: 'enumContractStatus',
      render: (statusId: number) => {
        const statusName = contractStatuses.find((status: any) => status?.id === statusId)?.name ?? '-';

        return (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignContent: 'center',
            }}
          >
            <Tag color={'green'}>{statusName}</Tag>
          </div>
        );
      },
    },
    {
      title: 'Actiune',
      dataIndex: '',
      key: 'actions',
      render: (record: any) => {
        const overlay = (
          <div className='circle-menu'>
            <Tooltip title='Modifica'>
              <Button
                className='circle-menu-item'
                icon={<EditOutlined />}
                onClick={() =>
                  handleOptionClick(() => window.open(`/service-maintenance/contract/edit?id=${record?.id}`, '_blank'))
                }
              />
            </Tooltip>
            <Tooltip title='Act Aditional'>
              <Button
                className='circle-menu-item'
                icon={<FileAddOutlined />}
                onClick={() =>
                  handleOptionClick(() =>
                    window.open(`/service-maintenance/contract/act-additionally/edit?id=${record?.id}`, '_blank'),
                  )
                }
              />
            </Tooltip>
            <Tooltip title='Incarca Imagine Contract'>
              <div className='circle-menu-item'>
                <Upload {...getUploadProps(record.id)}>
                  <Button
                    icon={<UploadOutlined />}
                    style={{ borderRadius: '50%', width: 40, height: 40, backgroundColor: '#e1e1e1' }}
                  />
                </Upload>
              </div>
            </Tooltip>
            <Tooltip title='Informatii'>
              <Button
                className='circle-menu-item'
                icon={<InfoCircleOutlined />}
                onClick={() => handleOptionClick(() => handleModalInformationOpen(record))}
              />
            </Tooltip>
            {!record?.haveFile ? (
              <Tooltip title='Descarca PDF'>
                <Button
                  className='circle-menu-item'
                  icon={<DownloadOutlined />}
                  onClick={() => handleOptionClick(() => printToPdf(record))}
                />
              </Tooltip>
            ) : (
              <Tooltip title='Descarca Contract Semnat'>
                <Button
                  className='circle-menu-item'
                  icon={<DownloadOutlined />}
                  onClick={() => handleOptionClick(() => downloadContract(record))}
                />
              </Tooltip>
            )}
          </div>
        );

        return (
          <div>
            {visibleMenuRecord === record.id && overlay}
            <Button onClick={() => handleMenuVisibility(record.id)} className='plus_edit'>
              {visibleMenuRecord === record.id ? <CompressOutlined /> : <PlusOutlined />}
            </Button>
          </div>
        );
      },
      width: 200,
      fixed: 'right',
    },
  ];

  if (
    isContractsLoading ||
    isPrintingContract ||
    isDownloadingPmContract ||
    isAdditionalActLoading ||
    isUpdatingAllContract ||
    isCompaniesLoading ||
    isContractStatusesLoading
  ) {
    return <Spin size='large' className='spinner' />;
  }

  return (
    <>
      <Row justify='space-between' align='middle' style={{ marginBottom: '20px' }}>
        <Col>
          <h1>Contracte SM</h1>
        </Col>
        {hasAdminPermission && (
          <Col>
            <AnimateButton
              type='primary'
              onClick={() => handleModifyAllContracts()}
              style={{
                backgroundColor: '#174279',
              }}
            >
              Modificati toate contractele
            </AnimateButton>
          </Col>
        )}
        <Col>
          <Tooltip title='Creeaza contract' trigger={'hover'} placement='left'>
            <Button
              type='primary'
              onClick={() => window.open(`/service-maintenance/contract/new`, '_blank')}
              icon={<PlusOutlined />}
              shape='circle'
            />
          </Tooltip>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
        <Col xs={24} sm={12} md={8}>
          <SearchInput
            placeholder='Search...'
            defaultValue={searchTerm}
            onChange={(e: any) => handleSearch(e.target.value)}
            autoFocus={true}
            style={{ width: '100%' }} // Ensures input takes full width
          />
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Select
            placeholder='Filtreaza dupa beneficiar'
            value={selectedCompany}
            onChange={handleCompanyChange}
            style={{ width: '100%' }}
            allowClear
            showSearch
            optionFilterProp='children'
          >
            {companies?.map((company: any) => (
              <Select.Option key={company?.id} value={company?.name}>
                {company?.name}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Select
            placeholder='Filtreaza dupa status'
            value={selectedStatus}
            onChange={handleStatusChange}
            style={{ width: '100%' }}
            allowClear
            showSearch
            optionFilterProp='children'
          >
            {contractStatuses?.map((status: any) => (
              <Select.Option key={status?.id} value={status?.name}>
                {status?.name}
              </Select.Option>
            ))}
          </Select>
        </Col>
      </Row>

      <ContractInformation
        handleModalInformationClose={handleModalInformationClose}
        modalInformation={modalInformation}
        selectedRecord={selectedRecord}
        selectedRecordAdditionalAct={selectedRecordAdditionalAct}
      />

      {isMobile ? (
        <div className='table-mobile-view'>
          {contracts.map((contract: any) => (
            <div className='mobile-view-background' key={contract.id}>
              <div className='mobile-view-header'>
                <Tag color='purple'>{contract.number}</Tag>
                <Button
                  onClick={() => handleMenuVisibility(contract.id)}
                  shape='circle'
                  icon={visibleMenuRecord === contract.id ? <CompressOutlined /> : <PlusOutlined />}
                />
              </div>

              {/* contract Details */}
              <Col span={24} className='CardMobile-item'>
                <strong className='CardMobile-item-label'>Client:</strong>
                <Tooltip title={contract.company?.name}>
                  <Tag className='client-tag' color='blue'>
                    {' '}
                    {contract.company?.name}
                  </Tag>
                </Tooltip>
              </Col>
              <Col span={24} className='CardMobile-item'>
                <strong className='CardMobile-item-label'>Data Contract:</strong>
                <Tag color={'blue'}>{moment(contract.date).format('DD-MM-YYYY')}</Tag>
              </Col>
              <Col span={24} className='CardMobile-item'>
                <strong className='CardMobile-item-label'>Data Inceput:</strong>
                {moment(contract.dateStart).format('DD-MM-YYYY')}
              </Col>
              <Col span={24} className='CardMobile-item'>
                <strong className='CardMobile-item-label'>Data Sfarsit:</strong>
                {moment(contract.dateEnd).format('DD-MM-YYYY')}
              </Col>
              <Col span={24} className='CardMobile-item'>
                <strong className='CardMobile-item-label'>Perioada (Zile):</strong>
                <Tag color={'blue'}> {contract.datePeriod}</Tag>
              </Col>
              <Col span={24} className='CardMobile-item'>
                <strong className='CardMobile-item-label'>Sisteme:</strong>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'end' }}>
                  {(() => {
                    const uniqueSystemTypeNames = Array.from(
                      new Set(
                        (contract.contractWorkPlaceItems ?? [])
                          .map((item: any) => item.systemType?.name)
                          .filter((name: string | undefined) => !!name),
                      ),
                    );

                    const displayCount = 3;
                    const shouldShowEllipsis = uniqueSystemTypeNames.length > displayCount;
                    const displayNames = shouldShowEllipsis
                      ? uniqueSystemTypeNames.slice(0, displayCount)
                      : uniqueSystemTypeNames;
                    const fullList = uniqueSystemTypeNames.join(', ');

                    return (
                      <Tooltip title={fullList}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {displayNames.map((name: any, index: any) => (
                            <Tag
                              key={index}
                              color='blue'
                              style={{
                                maxWidth: '120px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                cursor: 'pointer',
                              }}
                            >
                              {name}
                            </Tag>
                          ))}
                          {shouldShowEllipsis && (
                            <Tag
                              color='blue'
                              style={{
                                maxWidth: '120px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                cursor: 'pointer',
                              }}
                            >
                              ...
                            </Tag>
                          )}
                        </div>
                      </Tooltip>
                    );
                  })()}
                </div>
              </Col>
              <Col span={24} className='CardMobile-item'>
                <strong className='CardMobile-item-label'>Status:</strong>
                <Tag color='green'>{contract.enumContractStatus?.name}</Tag>
              </Col>

              {/* Action Buttons */}
              {visibleMenuRecord === contract.id && (
                <div className='visible-menu-action-btn'>
                  <Tooltip title='Modifica'>
                    <Button
                      className='modify'
                      shape='circle'
                      icon={<EditOutlined />}
                      onClick={() => window.open(`/service-maintenance/contract/edit?id=${contract.id}`, '_blank')}
                    />
                  </Tooltip>
                  <Tooltip title='Act Aditional'>
                    <Button
                      className='additional'
                      shape='circle'
                      icon={<FileAddOutlined />}
                      onClick={() =>
                        window.open(`/service-maintenance/contract/act-additionally/edit?id=${contract?.id}`, '_blank')
                      }
                    />
                  </Tooltip>
                  <Tooltip title='Incarca Imagine Contract'>
                    <Upload {...getUploadProps(contract.id)}>
                      <Button className='upload' shape='circle' icon={<UploadOutlined />} />
                    </Upload>
                  </Tooltip>
                  <Tooltip title='Informatii'>
                    <Button
                      className='information'
                      shape='circle'
                      icon={<InfoCircleOutlined />}
                      onClick={() => handleModalInformationOpen(contract)}
                    />
                  </Tooltip>
                  <Tooltip title='Descarca PDF'>
                    <Button
                      className='pdfDownload'
                      shape='circle'
                      icon={<DownloadOutlined />}
                      onClick={() => printToPdf(contract)}
                    />
                  </Tooltip>
                </div>
              )}
            </div>
          ))}

          {/* Pagination for Mobile */}
          <Pagination
            className='mobile-view-pagination'
            current={currentPage}
            pageSize={take}
            total={reduxContract.props.totalItems}
            onChange={(page, pageSize) => {
              dispatch(updateProps({ field: 'skip', value: (page - 1) * pageSize }));
              dispatch(updateProps({ field: 'take', value: pageSize }));
            }}
            showSizeChanger={true}
            pageSizeOptions={pageSizes.map((size) => size.toString())}
            onShowSizeChange={(_current, size) => {
              dispatch(updateProps({ field: 'skip', value: 0 }));
              dispatch(updateProps({ field: 'take', value: size }));
            }}
            showLessItems
          />
        </div>
      ) : (
        // Desktop View (Table)
        <Table
          dataSource={contracts}
          pagination={paginationConfig}
          columns={columns}
          rowKey='id'
          scroll={{ x: true }}
          className='large-view-table'
          onChange={(_pagination: any, _filters: any, sorter: any) => {
            setSortField(sorter.field);
            setSortOrder(sorter.order);
          }}
        />
      )}
    </>
  );
};

export default ContractPage;
